import { instance } from '@/api/instances'
import { TIMELINE_PHYSICIAN_TYPE } from '@/models/timeline'

const fetchTimeline = async (store, { signal, range }) => {
  try {
    const filters = getTimelineFilters(
      store.state.selectedFilters,
      store.state.otherFilters
    )
    const params = {
      range: JSON.stringify(range),
      filter: JSON.stringify(filters)
    }

    const { timelineItems, drafts } = await instance.get(
      `persons/${store.rootState.pillars.personId}/v2/timeline`,
      {
        params,
        signal
      }
    )

    return { drafts, items: timelineItems }
  } catch (error) {
    if (error instanceof TypeError) {
      store.commit(
        'errors/setError',
        'Ops, erro ao recuperar algumas informações da timeline',
        { root: true }
      )
    } else if (error.name !== 'AbortError') {
      store.commit(
        'errors/setCriticalError',
        { context: 'timeline', type: 'server' },
        { root: true }
      )
    }
    return null
  }
}

const getTimelineFilters = (filters, otherFilters) => {
  const params = {
    type: getTypeFilter(filters.type),
    staff: getPhysicianFilter(filters.physician, TIMELINE_PHYSICIAN_TYPE.STAFF),
    specialist: getPhysicianFilter(
      filters.physician,
      TIMELINE_PHYSICIAN_TYPE.SPECIALIST
    ),
    specialty: filters.healthCommunitySpecialistSpeciality?.map(
      specialty => specialty.id
    ),
    channel: filters.channel?.map(channel => channel.id),
    model_id: otherFilters?.filterIds
  }
  return params
}

const getTypeFilter = types => {
  return types?.reduce((acc, current) => {
    acc = acc.concat(current.filterValue)
    return acc
  }, [])
}

const getPhysicianFilter = (physicians, type) => {
  return physicians
    ?.filter(physician => physician.type === type)
    .map(physician => physician.id)
}

const fetchAppointmentDetails = async (store, appointmentId) => {
  try {
    const appointment = await instance.get(
      `appointments/${appointmentId}?load_tasks=true`
    )

    return appointment
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/timeline/fetchAppointmentDetails' },
      { root: true }
    )
  }
}

const fetchExcuseNoteById = async (store, excuseNoteId) => {
  try {
    const excuseNote = await instance.get(
      `appointments/excuse_notes/${excuseNoteId}`
    )

    return excuseNote
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'appointment/excuseNote/fetchById' },
      { root: true }
    )
  }
}

const fetchAppointmentCommunityDetails = async (store, appointmentId) => {
  try {
    const appointment = await instance.get(`counter_referrals/${appointmentId}`)

    return appointment
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/timeline/fetchAppointmentCommunityDetails'
      },
      { root: true }
    )
  }
}

const fetchAEmergencyDetails = async (store, appointmentId) => {
  try {
    const appointment = await instance.get(
      `persons/${store.rootState.pillars.personId}/tertiary_intention_touch_point/${appointmentId}`
    )

    return appointment
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/timeline/fetchAEmergencyDetails' },
      { root: true }
    )
  }
}

const fetchHospitalizationDetails = async (store, appointmentId) => {
  try {
    const appointment = await instance.get(
      `persons/${store.rootState.pillars.personId}/hospitalization_attendances/${appointmentId}`
    )

    return appointment
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/timeline/fetchHospitalizationDetails' },
      { root: true }
    )
  }
}

const fetchSurgeryDetails = async (store, surgeryId) => {
  try {
    const surgery = await instance.get(
      `persons/${store.rootState.pillars.personId}/surgery_attendances/${surgeryId}`
    )

    return surgery
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/timeline/fetchSurgeryDetails' },
      { root: true }
    )
  }
}

const fetchHealthFormDetails = async (store, formId) => {
  try {
    const form = await instance.get(
      `persons/${store.rootState.pillars.personId}/health_form/health_form_answer_group/${formId}`
    )

    return form
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/timeline/fetchHealthFormDetails' },
      { root: true }
    )
  }
}

const fetchThirdPartyDetails = async (store, { modelId, modelClass }) => {
  try {
    const params = { origin: modelClass }
    const thirdParty = await instance.get(
      `persons/${store.rootState.pillars.personId}/third_party_appointments/${modelId}`,
      { params }
    )

    return thirdParty
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/timeline/fetchThirdPartyDetails' },
      { root: true }
    )
  }
}

const publishEvolution = async (store, { appointmentId, description }) => {
  try {
    const newEvolution = await instance.post(
      `appointments/${appointmentId}/v2/evolutions`,
      {
        json: { description }
      }
    )

    return newEvolution
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/timeline/publishEvolution' },
      { root: true }
    )
  }
}

const postAiReview = async (store, { timelineId, score }) => {
  try {
    await instance.post(`timelines/${timelineId}/ai-summary-review`, {
      json: { score }
    })
    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/timeline/postAiReview' },
      { root: true }
    )
    return false
  }
}

/**
 * Set filter by other pillar
 * filterType: the name of the filter (key object)
 * filterValue: the value of the filter (can be a name, id object)
 * disableTooltipText: the value of tooltip disabled filter
 * @param {*} store
 * @param {Object} param
 */
const setOtherFilters = (
  store,
  { filterTitle, filterName, filterIds, disableTooltipText }
) => {
  store.commit('clearOtherFilters')
  store.commit('setOtherFilters', {
    filterTitle,
    filterName,
    filterIds,
    disableTooltipText
  })
}

const clearOtherFilters = store => {
  store.commit('clearOtherFilters')
}

const setFilter = (store, { filterType, filterValue }) => {
  store.commit('setFilter', { filterType, filterValue })
}

const clearFilters = store => {
  store.commit('clearFilters')
  store.commit('clearOtherFilters')
}

export default {
  fetchTimeline,
  fetchAppointmentDetails,
  fetchAppointmentCommunityDetails,
  fetchHospitalizationDetails,
  fetchThirdPartyDetails,
  fetchHealthFormDetails,
  fetchSurgeryDetails,
  fetchAEmergencyDetails,
  publishEvolution,
  postAiReview,
  setOtherFilters,
  clearFilters,
  clearOtherFilters,
  setFilter,
  fetchExcuseNoteById
}
